import React from "react";
import {
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  Button,
  Icon,
} from "semantic-ui-react";
import ComplianceModal from "../modals/ComplianceModal";
import NewReviewQuestionModal from "../modals/NewReviewQuestionModal";
import EscalatedReviewTable from "./EscalatedReviewTable";
import { getSelectedModuleData } from "@actions/compliance";
import { camelCaseToText } from "../../helpers";

const ComplianceButtons = (props) => {
  const isReviewQuestion = props.type === "ReviewQuestion";
  return (
    <div className="mt-1">
      <div className="flex-row gap-1 justify-end">
        <Button
          color="green"
          onClick={props.handleNew}
          disabled={props.loading || !props.type || !props.canAdd}
          icon="save"
        >
          New
        </Button>
        <Button
          color="green"
          onClick={props.handleEdit}
          disabled={props.loading || !props.selectedRowData}
          icon="save"
        >
          Edit
        </Button>
        {!isReviewQuestion && (
          <Button
            color="red"
            onClick={props.handleDelete}
            icon="delete"
            disabled={!props.canDelete}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

class ComplianceTable extends React.Component {
  state = {
    activeIndex: null,
    selectedRowData: null,
    showModal: false,
  };

  getFields = (data) => {
    const keys = Object.keys(data).filter((key) => {
      const value = data[key];
      return (
        (typeof value !== "object" &&
          key !== "rowVersion" &&
          key !== "id" &&
          key !== "lastUpdated") ||
        value === null
      );
    });
    return keys;
  };

  handleNew = () => {
    this.setState({ selectedRowData: null }, () =>
      this.setState({ showModal: true }),
    );
  };

  handleDelete = () => {
    this.props.onDelete(this.state.selectedRowData.id);
  };

  handleEdit = async () => {
    const data = await getSelectedModuleData(
      this.props.type,
      this.state.selectedRowData.id,
    );
    this.setState({ selectedRowData: data, showModal: true });
  };

  handleSelect = (data) => () => {
    this.setState({ selectedRowData: data });
  };

  onClose = () => {
    this.setState({ showModal: false, selectedRowData: null });
  };

  onSave = async (data) => {
    await this.props.onSave(data);
  };

  mapFieldNames = (field) => {
    switch (field) {
      case "customerStateName":
        return "customerState";
      case "isDisabled":
        return "isDisabled";
      case "ordinal":
        return "ordinal";
      case "eventType":
        return null;
      case "eventTypeId":
        return "eventType";
      case "incidentType":
        return null;
      case "incidentTypeId":
        return "incidentType";
      default:
        return field;
    }
  };

  isRadioField = (_field, value) => {
    return typeof value === "boolean";
  };

  render() {
    const isReviewQuestion = this.props.type === "ReviewQuestion";
    return (
      <>
        <ComplianceButtons
          {...this.state}
          {...this.props}
          handleNew={this.handleNew}
          handleDelete={this.handleDelete}
          handleEdit={this.handleEdit}
        />
        {isReviewQuestion ? (
          <EscalatedReviewTable
            data={this.props.data}
            selectedRowId={this.state.selectedRowData?.id}
            onSelectRow={(row) => this.handleSelect(row)()}
          />
        ) : (
          <Table singleLine>
            <TableHeader>
              <TableRow>
                {this.getFields(this.props.data[0]).map(
                  (field, index) => {
                    const mappedField = this.mapFieldNames(field);
                    if (mappedField == null) return;
                    return (
                      <TableHeaderCell
                        textAlign={index === 0 ? "left" : "center"}
                        className="capitalize-first-letter"
                        key={mappedField}
                      >
                        {camelCaseToText(mappedField)}
                      </TableHeaderCell>
                    )
                  })
                }
              </TableRow>
            </TableHeader>
            <TableBody>
              {this.props.data.slice().sort((a, b) => a.ordinal - b.ordinal).map((data) => (
                <TableRow
                  key={data.id}
                  className="clickable"
                  onClick={this.handleSelect(data)}
                  active={this.state.selectedRowData?.id === data.id}
                >
                  {this.getFields(this.props.data[0]).map(
                    (field, index) => {
                      const mappedField = this.mapFieldNames(field);
                      if (mappedField == null) return;
                      // Get the value for this field
                      let value = data[mappedField];

                      // Check if value is an object (and not null)
                      if (
                        value &&
                        typeof value === "object" &&
                        !Array.isArray(value)
                      ) {
                        // Extract the name property from nested objects
                        value = value.name ?? "-";
                      }

                      if (mappedField == "incidentTypeId") {
                        value = this.props.incidentTypes.find(x => x.id == value)?.name
                      }

                      if (mappedField == "eventTypeId") {
                        value = this.props.eventTypes.find(x => x.id == value)?.name
                      }

                      if (mappedField == "customerState") {
                        value = data?.customerStateName ?? "-";
                      }

                      return (
                        <TableCell
                          key={`cell-${index}`}
                          textAlign={index === 0 ? "left" : "center"}
                        >
                          {this.isRadioField(field, value) ? (
                            <Icon
                              name={value ? "check" : "close"}
                              color={value ? "green" : "red"}
                            />
                          ) : (
                            value ?? "-"
                          )}
                        </TableCell>
                      );
                    },
                  )}
                </TableRow>
              ))
              
              }
            </TableBody>
          </Table>
        )}
        {this.props.type === "ReviewQuestion" && this.state.showModal ? (
          <NewReviewQuestionModal
            open={this.state.showModal}
            onClose={this.onClose}
            onSave={this.onSave}
            questions={this.props.data}
            initialData={this.state.selectedRowData}
            reviewTypeId={this.props.reviewTypeId}
          />
        ) : (
          <ComplianceModal
            title={
              !this.state.selectedRowData
                ? `Create New ${this.props.name}`
                : `Edit ${this.props.name}`
            }
            open={this.state.showModal}
            type={this.props.type}
            data={this.state.selectedRowData}
            model={this.props.model}
            eventTypes={this.props.eventTypes}
            incidentTypes={this.props.incidentTypes}
            onClose={this.onClose}
            onSave={this.onSave}
          />
        )}
      </>
    );
  }
}

export default ComplianceTable;
